import { QueryClientProvider } from '@tanstack/react-query'
import { lazy, Suspense, useState } from 'react'
import { BrowserRouter, Navigate, Route, Routes } from 'react-router-dom'
import { RouteFallback } from '@/components/common/route-fallback'
import { createQueryClient } from '@/lib/query-client'
import { AnonymousLandingPage } from '@/routes/anonymous-landing-page'
import { HomePage } from '@/routes/home-page'
import { NotFoundPage } from '@/routes/not-found-page'
import { RootLayout } from '@/routes/root-layout'
import { SignedInLandingPage } from '@/routes/signed-in-landing-page'

// Lazy-loaded so the initial bundle carries only the shell (layout, router,
// query client) — every route below is its own chunk, fetched on first
// visit rather than upfront. `RootLayout`/`AnonymousLandingPage`/
// `NotFoundPage` stay eager: they're on the critical path for the most
// common entry (`/`) and for any unmatched route.
const EventsPage = lazy(() =>
  import('@/routes/events-page').then((m) => ({ default: m.EventsPage })),
)
const EventShuttleConfirmPage = lazy(() =>
  import('@/routes/event-shuttle-confirm-page').then((m) => ({
    default: m.EventShuttleConfirmPage,
  })),
)
const EventMyShuttlePage = lazy(() =>
  import('@/routes/event-my-shuttle-page').then((m) => ({ default: m.EventMyShuttlePage })),
)
const EventStaffCheckinPage = lazy(() =>
  import('@/routes/event-staff-checkin-page').then((m) => ({ default: m.EventStaffCheckinPage })),
)
const EventMealsPage = lazy(() =>
  import('@/routes/event-meals-page').then((m) => ({ default: m.EventMealsPage })),
)
const EventHelpPage = lazy(() =>
  import('@/routes/event-help-page').then((m) => ({ default: m.EventHelpPage })),
)
const EventRosterPage = lazy(() =>
  import('@/routes/event-roster-page').then((m) => ({ default: m.EventRosterPage })),
)
const EventAdminPage = lazy(() =>
  import('@/routes/event-admin-page').then((m) => ({ default: m.EventAdminPage })),
)
const EventVolunteerPage = lazy(() =>
  import('@/routes/event-volunteer-page').then((m) => ({ default: m.EventVolunteerPage })),
)
const EventPresenterPage = lazy(() =>
  import('@/routes/event-presenter-page').then((m) => ({ default: m.EventPresenterPage })),
)
const EventAdminOperationsPage = lazy(() =>
  import('@/routes/event-admin-operations-page').then((m) => ({
    default: m.EventAdminOperationsPage,
  })),
)
const EventPage = lazy(() => import('@/routes/event-page').then((m) => ({ default: m.EventPage })))
const AboutPage = lazy(() => import('@/routes/about-page').then((m) => ({ default: m.AboutPage })))
const HowToPage = lazy(() => import('@/routes/howto-page').then((m) => ({ default: m.HowToPage })))
const ArchitecturePage = lazy(() =>
  import('@/routes/architecture-page').then((m) => ({ default: m.ArchitecturePage })),
)
const TrustPage = lazy(() => import('@/routes/trust-page').then((m) => ({ default: m.TrustPage })))
const PrivacyPage = lazy(() =>
  import('@/routes/privacy-page').then((m) => ({ default: m.PrivacyPage })),
)
const TermsPage = lazy(() => import('@/routes/terms-page').then((m) => ({ default: m.TermsPage })))
const DataPolicyPage = lazy(() =>
  import('@/routes/data-policy-page').then((m) => ({ default: m.DataPolicyPage })),
)
const GuideStudentPage = lazy(() =>
  import('@/routes/guide-student-page').then((m) => ({ default: m.GuideStudentPage })),
)
const GuideJudgePage = lazy(() =>
  import('@/routes/guide-judge-page').then((m) => ({ default: m.GuideJudgePage })),
)
const GuideOrganizerPage = lazy(() =>
  import('@/routes/guide-organizer-page').then((m) => ({ default: m.GuideOrganizerPage })),
)
const GuideAdminPage = lazy(() =>
  import('@/routes/guide-admin-page').then((m) => ({ default: m.GuideAdminPage })),
)
const HelpHomePage = lazy(() =>
  import('@/routes/help-home-page').then((m) => ({ default: m.HelpHomePage })),
)
const HelpSearchPage = lazy(() =>
  import('@/routes/help-search-page').then((m) => ({ default: m.HelpSearchPage })),
)
const HelpArticlePage = lazy(() =>
  import('@/routes/help-article-page').then((m) => ({ default: m.HelpArticlePage })),
)
const CompetitionAuditPage = lazy(() =>
  import('@/routes/admin/competition-audit-page').then((m) => ({
    default: m.CompetitionAuditPage,
  })),
)
const CompetitionBuilderPage = lazy(() =>
  import('@/routes/admin/competition-builder-page').then((m) => ({
    default: m.CompetitionBuilderPage,
  })),
)
const CompetitionClonePage = lazy(() =>
  import('@/routes/admin/competition-clone-page').then((m) => ({
    default: m.CompetitionClonePage,
  })),
)
const CompetitionSaveAsTemplatePage = lazy(() =>
  import('@/routes/admin/competition-save-as-template-page').then((m) => ({
    default: m.CompetitionSaveAsTemplatePage,
  })),
)
const CompetitionCreateFromTemplatePage = lazy(() =>
  import('@/routes/admin/competition-create-from-template-page').then((m) => ({
    default: m.CompetitionCreateFromTemplatePage,
  })),
)
const CompetitionImportPage = lazy(() =>
  import('@/routes/admin/competition-import-page').then((m) => ({
    default: m.CompetitionImportPage,
  })),
)
const CompetitionCategoriesPage = lazy(() =>
  import('@/routes/admin/competition-categories-page').then((m) => ({
    default: m.CompetitionCategoriesPage,
  })),
)
const CompetitionFormEditorPage = lazy(() =>
  import('@/routes/admin/competition-form-editor-page').then((m) => ({
    default: m.CompetitionFormEditorPage,
  })),
)
const CompetitionFormsPage = lazy(() =>
  import('@/routes/admin/competition-forms-page').then((m) => ({
    default: m.CompetitionFormsPage,
  })),
)
const CompetitionFilesPage = lazy(() =>
  import('@/routes/admin/competition-files-page').then((m) => ({
    default: m.CompetitionFilesPage,
  })),
)
const CompetitionRegistrationDataPage = lazy(() =>
  import('@/routes/admin/competition-registration-data-page').then((m) => ({
    default: m.CompetitionRegistrationDataPage,
  })),
)
const CompetitionAnnouncementsPage = lazy(() =>
  import('@/routes/admin/competition-announcements-page').then((m) => ({
    default: m.CompetitionAnnouncementsPage,
  })),
)
const CompetitionEligibilityDataPage = lazy(() =>
  import('@/routes/admin/competition-eligibility-data-page').then((m) => ({
    default: m.CompetitionEligibilityDataPage,
  })),
)
const CompetitionExportCenterPage = lazy(() =>
  import('@/routes/admin/competition-export-center-page').then((m) => ({
    default: m.CompetitionExportCenterPage,
  })),
)
const CompetitionLayout = lazy(() =>
  import('@/routes/admin/competition-layout').then((m) => ({ default: m.CompetitionLayout })),
)
const CompetitionListPage = lazy(() =>
  import('@/routes/admin/competition-list-page').then((m) => ({
    default: m.CompetitionListPage,
  })),
)
const CompetitionOverviewPage = lazy(() =>
  import('@/routes/admin/competition-overview-page').then((m) => ({
    default: m.CompetitionOverviewPage,
  })),
)
const CompetitionPeoplePage = lazy(() =>
  import('@/routes/admin/competition-people-page').then((m) => ({
    default: m.CompetitionPeoplePage,
  })),
)
const CompetitionOperationsDashboardPage = lazy(() =>
  import('@/routes/admin/competition-operations-dashboard-page').then((m) => ({
    default: m.CompetitionOperationsDashboardPage,
  })),
)
const CompetitionEligibilityReviewsPage = lazy(() =>
  import('@/routes/admin/competition-eligibility-reviews-page').then((m) => ({
    default: m.CompetitionEligibilityReviewsPage,
  })),
)
const CompetitionParticipantsPage = lazy(() =>
  import('@/routes/admin/competition-participants-page').then((m) => ({
    default: m.CompetitionParticipantsPage,
  })),
)
const CompetitionJudgesPage = lazy(() =>
  import('@/routes/admin/competition-judges-page').then((m) => ({
    default: m.CompetitionJudgesPage,
  })),
)
const CompetitionJudgingLoadPage = lazy(() =>
  import('@/routes/admin/competition-judging-load-page').then((m) => ({
    default: m.CompetitionJudgingLoadPage,
  })),
)
const CompetitionManualScoresPage = lazy(() =>
  import('@/routes/admin/competition-manual-scores-page').then((m) => ({
    default: m.CompetitionManualScoresPage,
  })),
)
const CompetitionRoundsPage = lazy(() =>
  import('@/routes/admin/competition-rounds-page').then((m) => ({
    default: m.CompetitionRoundsPage,
  })),
)
const CompetitionRubricsPage = lazy(() =>
  import('@/routes/admin/competition-rubrics-page').then((m) => ({
    default: m.CompetitionRubricsPage,
  })),
)
const CompetitionSubmissionsPage = lazy(() =>
  import('@/routes/admin/competition-submissions-page').then((m) => ({
    default: m.CompetitionSubmissionsPage,
  })),
)
const CompetitionTeamMembersPage = lazy(() =>
  import('@/routes/admin/competition-team-members-page').then((m) => ({
    default: m.CompetitionTeamMembersPage,
  })),
)
const CompetitionResultsPage = lazy(() =>
  import('@/routes/admin/competition-results-page').then((m) => ({
    default: m.CompetitionResultsPage,
  })),
)
const BootstrapPage = lazy(() =>
  import('@/routes/bootstrap-page').then((m) => ({ default: m.BootstrapPage })),
)
const DevStatusPage = lazy(() =>
  import('@/routes/dev-status-page').then((m) => ({ default: m.DevStatusPage })),
)
const ForgotPasswordPage = lazy(() =>
  import('@/routes/forgot-password-page').then((m) => ({ default: m.ForgotPasswordPage })),
)
const LoginPage = lazy(() => import('@/routes/login-page').then((m) => ({ default: m.LoginPage })))
const ProfilePage = lazy(() =>
  import('@/routes/account/profile-page').then((m) => ({ default: m.ProfilePage })),
)
const SecurityPage = lazy(() =>
  import('@/routes/account/security-page').then((m) => ({ default: m.SecurityPage })),
)
const RolesPage = lazy(() =>
  import('@/routes/account/roles-page').then((m) => ({ default: m.RolesPage })),
)
const EmailVerifyPage = lazy(() =>
  import('@/routes/account/email-verify-page').then((m) => ({ default: m.EmailVerifyPage })),
)
const RegisterPage = lazy(() =>
  import('@/routes/register-page').then((m) => ({ default: m.RegisterPage })),
)
const ResetPasswordPage = lazy(() =>
  import('@/routes/reset-password-page').then((m) => ({ default: m.ResetPasswordPage })),
)
const StudentCompetitionPage = lazy(() =>
  import('@/routes/student/student-competition-page').then((m) => ({
    default: m.StudentCompetitionPage,
  })),
)
const StudentEligibilityPage = lazy(() =>
  import('@/routes/student/student-eligibility-page').then((m) => ({
    default: m.StudentEligibilityPage,
  })),
)
const StudentDashboardPage = lazy(() =>
  import('@/routes/student/student-dashboard-page').then((m) => ({
    default: m.StudentDashboardPage,
  })),
)
const StudentInvitePage = lazy(() =>
  import('@/routes/student/student-invite-page').then((m) => ({
    default: m.StudentInvitePage,
  })),
)
const StudentSubmissionPage = lazy(() =>
  import('@/routes/student/student-submission-page').then((m) => ({
    default: m.StudentSubmissionPage,
  })),
)
const StudentTeamPage = lazy(() =>
  import('@/routes/student/student-team-page').then((m) => ({ default: m.StudentTeamPage })),
)
const StudentResultPage = lazy(() =>
  import('@/routes/student/student-result-page').then((m) => ({ default: m.StudentResultPage })),
)
const StudentCompetitionInformationPage = lazy(() =>
  import('@/routes/student/student-competition-information-page').then((m) => ({
    default: m.StudentCompetitionInformationPage,
  })),
)
const StudentCompetitionLayout = lazy(() =>
  import('@/routes/student/student-competition-layout').then((m) => ({
    default: m.StudentCompetitionLayout,
  })),
)
const JudgeAssignmentsPage = lazy(() =>
  import('@/routes/judge/judge-assignments-page').then((m) => ({
    default: m.JudgeAssignmentsPage,
  })),
)
const JudgeScorePage = lazy(() =>
  import('@/routes/judge/judge-score-page').then((m) => ({ default: m.JudgeScorePage })),
)
const JudgeProfilePage = lazy(() =>
  import('@/routes/judge/judge-profile-page').then((m) => ({ default: m.JudgeProfilePage })),
)
const JudgeHistoryPage = lazy(() =>
  import('@/routes/judge/judge-history-page').then((m) => ({ default: m.JudgeHistoryPage })),
)
const PublicCompetitionPage = lazy(() =>
  import('@/routes/public-competition-page').then((m) => ({ default: m.PublicCompetitionPage })),
)
const CompetitionRequestsPage = lazy(() =>
  import('@/routes/competition-requests/competition-requests-page').then((m) => ({
    default: m.CompetitionRequestsPage,
  })),
)
const CompetitionRequestFormPage = lazy(() =>
  import('@/routes/competition-requests/competition-request-form-page').then((m) => ({
    default: m.CompetitionRequestFormPage,
  })),
)
const CompetitionRequestDetailPage = lazy(() =>
  import('@/routes/competition-requests/competition-request-detail-page').then((m) => ({
    default: m.CompetitionRequestDetailPage,
  })),
)
const PlatformLayout = lazy(() =>
  import('@/routes/platform/platform-layout').then((m) => ({ default: m.PlatformLayout })),
)
const PlatformDashboardPage = lazy(() =>
  import('@/routes/platform/platform-dashboard-page').then((m) => ({
    default: m.PlatformDashboardPage,
  })),
)
const PlatformUsersPage = lazy(() =>
  import('@/routes/platform/platform-users-page').then((m) => ({ default: m.PlatformUsersPage })),
)
const PlatformUserDetailPage = lazy(() =>
  import('@/routes/platform/platform-user-detail-page').then((m) => ({
    default: m.PlatformUserDetailPage,
  })),
)
const PlatformErasureRequestsPage = lazy(() =>
  import('@/routes/platform/platform-erasure-requests-page').then((m) => ({
    default: m.PlatformErasureRequestsPage,
  })),
)
const PlatformJudgesPage = lazy(() =>
  import('@/routes/platform/platform-judges-page').then((m) => ({ default: m.PlatformJudgesPage })),
)
const PlatformAuditPage = lazy(() =>
  import('@/routes/platform/platform-audit-page').then((m) => ({ default: m.PlatformAuditPage })),
)
const PlatformCompetitionsPickerPage = lazy(() =>
  import('@/routes/platform/platform-competitions-picker-page').then((m) => ({
    default: m.PlatformCompetitionsPickerPage,
  })),
)
const PlatformCompetitionPeoplePage = lazy(() =>
  import('@/routes/platform/platform-competition-people-page').then((m) => ({
    default: m.PlatformCompetitionPeoplePage,
  })),
)
const PlatformAdminsPage = lazy(() =>
  import('@/routes/platform/platform-admins-page').then((m) => ({ default: m.PlatformAdminsPage })),
)
const PlatformCompetitionRequestsPage = lazy(() =>
  import('@/routes/platform/platform-competition-requests-page').then((m) => ({
    default: m.PlatformCompetitionRequestsPage,
  })),
)
const PlatformCompetitionRequestDetailPage = lazy(() =>
  import('@/routes/platform/platform-competition-request-detail-page').then((m) => ({
    default: m.PlatformCompetitionRequestDetailPage,
  })),
)
const PlatformDiscoveryCategoriesPage = lazy(() =>
  import('@/routes/platform/platform-discovery-categories-page').then((m) => ({
    default: m.PlatformDiscoveryCategoriesPage,
  })),
)

export function App() {
  const [queryClient] = useState(createQueryClient)

  return (
    <QueryClientProvider client={queryClient}>
      <BrowserRouter>
        <Suspense fallback={<RouteFallback />}>
          <Routes>
            <Route element={<RootLayout />}>
              <Route path="/" element={<AnonymousLandingPage />} />
              <Route path="/app" element={<SignedInLandingPage />} />
              <Route path="/home" element={<HomePage />} />
              <Route path="/dev/status" element={<DevStatusPage />} />
              <Route path="/register" element={<RegisterPage />} />
              <Route path="/bootstrap" element={<BootstrapPage />} />
              <Route path="/login" element={<LoginPage />} />
              <Route path="/forgot-password" element={<ForgotPasswordPage />} />
              <Route path="/reset-password" element={<ResetPasswordPage />} />
              <Route path="/account/profile" element={<ProfilePage />} />
              <Route path="/account/security" element={<SecurityPage />} />
              <Route path="/account/roles" element={<RolesPage />} />
              <Route path="/account/email/verify" element={<EmailVerifyPage />} />
              <Route path="/competition-requests" element={<CompetitionRequestsPage />} />
              <Route path="/competition-requests/new" element={<CompetitionRequestFormPage />} />
              <Route
                path="/competition-requests/:requestId"
                element={<CompetitionRequestDetailPage />}
              />
              <Route path="/c/:slug" element={<PublicCompetitionPage />} />
              <Route path="/about" element={<AboutPage />} />
              <Route path="/events" element={<EventsPage />} />
              <Route path="/events/:slug" element={<EventPage />} />
              <Route path="/events/:slug/shuttle" element={<EventPage view="shuttle" />} />
              <Route path="/events/:slug/shuttle/confirm" element={<EventShuttleConfirmPage />} />
              <Route path="/events/:slug/my" element={<EventMyShuttlePage />} />
              <Route path="/events/:slug/meals" element={<EventMealsPage />} />
              <Route path="/events/:slug/help" element={<EventHelpPage />} />
              <Route path="/events/:slug/admin" element={<EventAdminPage />} />
              <Route path="/events/:slug/admin/operations" element={<EventAdminOperationsPage />} />
              <Route path="/events/:slug/volunteer" element={<EventVolunteerPage />} />
              <Route path="/events/:slug/presenter" element={<EventPresenterPage />} />
              <Route path="/events/:slug/staff/check-in" element={<EventStaffCheckinPage />} />
              <Route path="/events/:slug/staff/roster" element={<EventRosterPage />} />
              <Route path="/howto" element={<HowToPage />} />
              <Route path="/architecture" element={<ArchitecturePage />} />
              <Route path="/trust" element={<TrustPage />} />
              <Route path="/privacy" element={<PrivacyPage />} />
              <Route path="/terms" element={<TermsPage />} />
              <Route path="/data-policy" element={<DataPolicyPage />} />
              <Route path="/guide/student" element={<GuideStudentPage />} />
              <Route path="/guide/judge" element={<GuideJudgePage />} />
              <Route path="/guide/organizer" element={<GuideOrganizerPage />} />
              <Route path="/guide/admin" element={<GuideAdminPage />} />
              <Route path="/help" element={<HelpHomePage />} />
              <Route path="/help/search" element={<HelpSearchPage />} />
              <Route
                path="/help/student/final-submit"
                element={<HelpArticlePage articleId="student-final-submit" />}
              />
              <Route
                path="/help/troubleshooting/submission-failed"
                element={<HelpArticlePage articleId="student-submission-failed" />}
              />
              <Route
                path="/help/organizer/invite-judge"
                element={<HelpArticlePage articleId="organizer-invite-judge" />}
              />
              <Route
                path="/help/organizer/reopen-score"
                element={<HelpArticlePage articleId="organizer-reopen-score" />}
              />
              <Route
                path="/help/reference/statuses"
                element={<HelpArticlePage articleId="status-reference" />}
              />
              <Route
                path="/help/student/create-join-team"
                element={<HelpArticlePage articleId="student-create-join-team" />}
              />
              <Route
                path="/help/judge/accept-invitation"
                element={<HelpArticlePage articleId="judge-accept-invitation" />}
              />
              <Route
                path="/help/judge/submit-score"
                element={<HelpArticlePage articleId="judge-submit-score" />}
              />
              <Route
                path="/help/organizer/manage-submissions"
                element={<HelpArticlePage articleId="organizer-manage-submissions" />}
              />
              <Route
                path="/help/admin/dashboard"
                element={<HelpArticlePage articleId="platform-admin-dashboard" />}
              />
              <Route
                path="/help/student/fill-registration-form"
                element={<HelpArticlePage articleId="student-fill-registration-form" />}
              />
              <Route
                path="/help/judge/declare-conflict-of-interest"
                element={<HelpArticlePage articleId="judge-declare-conflict-of-interest" />}
              />
              <Route
                path="/help/organizer/assign-submissions"
                element={<HelpArticlePage articleId="organizer-assign-submissions" />}
              />
              <Route
                path="/help/organizer/publish-results"
                element={<HelpArticlePage articleId="organizer-publish-results" />}
              />
              <Route
                path="/help/admin/manage-administrators"
                element={<HelpArticlePage articleId="platform-admin-manage-administrators" />}
              />
              <Route
                path="/help/student/upload-and-mark-ready"
                element={<HelpArticlePage articleId="student-upload-and-mark-ready" />}
              />
              <Route
                path="/help/judge/view-assignments"
                element={<HelpArticlePage articleId="judge-view-assignments" />}
              />
              <Route
                path="/help/judge/profile-and-history"
                element={<HelpArticlePage articleId="judge-profile-and-history" />}
              />
              <Route
                path="/help/judge/decline-or-withdraw"
                element={<HelpArticlePage articleId="judge-decline-or-withdraw" />}
              />
              <Route
                path="/help/organizer/auto-distribute"
                element={<HelpArticlePage articleId="organizer-auto-distribute" />}
              />
              <Route
                path="/help/troubleshooting/judge-score-submission-failed"
                element={<HelpArticlePage articleId="judge-score-submission-failed" />}
              />
              <Route
                path="/help/organizer/request-new-competition"
                element={<HelpArticlePage articleId="organizer-request-new-competition" />}
              />
              <Route
                path="/help/admin/user-management"
                element={<HelpArticlePage articleId="platform-admin-user-management" />}
              />
              <Route
                path="/help/student/create-account-and-verify-email"
                element={<HelpArticlePage articleId="student-create-account-and-verify-email" />}
              />
              <Route
                path="/help/organizer/configure-round-schedule"
                element={<HelpArticlePage articleId="organizer-configure-round-schedule" />}
              />
              <Route
                path="/help/organizer/configure-categories"
                element={<HelpArticlePage articleId="organizer-configure-categories" />}
              />
              <Route
                path="/help/organizer/build-forms"
                element={<HelpArticlePage articleId="organizer-build-forms" />}
              />
              <Route
                path="/help/organizer/create-rubric"
                element={<HelpArticlePage articleId="organizer-create-rubric" />}
              />
              <Route
                path="/help/organizer/view-scores"
                element={<HelpArticlePage articleId="organizer-view-scores" />}
              />
              <Route
                path="/help/organizer/manual-scoring"
                element={<HelpArticlePage articleId="organizer-manual-scoring" />}
              />
              <Route
                path="/help/reference/events-overview"
                element={<HelpArticlePage articleId="reference-events-overview" />}
              />
              <Route
                path="/help/student/book-event-shuttle"
                element={<HelpArticlePage articleId="student-book-event-shuttle" />}
              />
              <Route
                path="/help/student/event-meals-and-help-desk"
                element={<HelpArticlePage articleId="student-event-meals-and-help-desk" />}
              />
              <Route
                path="/help/troubleshooting/event-shuttle-booking-failed"
                element={<HelpArticlePage articleId="student-event-shuttle-booking-failed" />}
              />
              <Route
                path="/help/organizer/event-day-check-in"
                element={<HelpArticlePage articleId="organizer-event-day-check-in" />}
              />
              <Route
                path="/help/organizer/event-console-and-members"
                element={<HelpArticlePage articleId="organizer-event-console-and-members" />}
              />
              <Route
                path="/help/organizer/advance-teams"
                element={<HelpArticlePage articleId="organizer-advance-teams" />}
              />
              <Route
                path="/help/organizer/pre-launch-checklist"
                element={<HelpArticlePage articleId="organizer-pre-launch-checklist" />}
              />
              <Route
                path="/help/admin/review-competition-requests"
                element={<HelpArticlePage articleId="platform-admin-review-competition-requests" />}
              />
              <Route
                path="/help/admin/audit"
                element={<HelpArticlePage articleId="platform-admin-audit" />}
              />
              <Route
                path="/help/student/upload-eligibility-proof"
                element={<HelpArticlePage articleId="student-upload-eligibility-proof" />}
              />
              <Route
                path="/help/organizer/review-eligibility"
                element={<HelpArticlePage articleId="organizer-review-eligibility" />}
              />
              <Route
                path="/help/troubleshooting/eligibility-proof-rejected"
                element={<HelpArticlePage articleId="student-eligibility-proof-rejected" />}
              />
              <Route
                path="/help/student/getting-started"
                element={<HelpArticlePage articleId="student-getting-started" />}
              />
              <Route
                path="/help/organizer/getting-started"
                element={<HelpArticlePage articleId="organizer-getting-started" />}
              />
              <Route
                path="/help/judge/getting-started"
                element={<HelpArticlePage articleId="judge-getting-started" />}
              />
              <Route
                path="/help/organizer/operations-dashboard"
                element={<HelpArticlePage articleId="organizer-operations-dashboard" />}
              />
              <Route
                path="/help/reference/organizer-participants-page"
                element={<HelpArticlePage articleId="organizer-participants-page" />}
              />
              <Route
                path="/help/student/registration-needs-update"
                element={<HelpArticlePage articleId="student-registration-needs-update" />}
              />
              <Route
                path="/help/organizer/manage-announcements"
                element={<HelpArticlePage articleId="organizer-manage-announcements" />}
              />
              <Route
                path="/help/organizer/registration-reminders"
                element={<HelpArticlePage articleId="organizer-registration-reminders" />}
              />
              <Route
                path="/help/organizer/notification-emails"
                element={<HelpArticlePage articleId="organizer-notification-emails" />}
              />
              <Route
                path="/help/reference/organizer-registration-data-page"
                element={<HelpArticlePage articleId="organizer-registration-data-page" />}
              />
              <Route
                path="/help/reference/organizer-eligibility-data-page"
                element={<HelpArticlePage articleId="organizer-eligibility-data-page" />}
              />
              <Route
                path="/help/organizer/judging-load-page"
                element={<HelpArticlePage articleId="organizer-judging-load-page" />}
              />
              <Route
                path="/help/troubleshooting/organizer-judging-load-coi"
                element={<HelpArticlePage articleId="organizer-judging-load-coi-troubleshooting" />}
              />
              <Route
                path="/help/organizer/export-center"
                element={<HelpArticlePage articleId="organizer-export-center" />}
              />
              <Route
                path="/help/admin/getting-started"
                element={<HelpArticlePage articleId="platform-admin-getting-started" />}
              />
              <Route path="/student" element={<StudentDashboardPage />} />
              <Route path="/student/competitions" element={<StudentDashboardPage />} />
              {/* PR-269 (Student Participant UI/UX/IA v4.45, Wave D): every
                  Participant Competition page nests under the shared
                  Workspace Shell (Sidebar/Drawer) instead of being five
                  independent top-level routes. Same URLs as before this
                  change (no bookmark 404s) — `StudentCompetitionLayout`
                  supplies chrome only, never a page's own heading/content. */}
              <Route
                path="/student/competitions/:competitionId"
                element={<StudentCompetitionLayout />}
              >
                <Route index element={<StudentCompetitionPage />} />
                <Route path="team" element={<StudentTeamPage />} />
                <Route path="eligibility" element={<StudentEligibilityPage />} />
                <Route path="submission" element={<StudentSubmissionPage />} />
                <Route path="result" element={<StudentResultPage />} />
                <Route path="information" element={<StudentCompetitionInformationPage />} />
              </Route>
              <Route path="/student/invites/:token" element={<StudentInvitePage />} />
              <Route path="/judge" element={<JudgeAssignmentsPage />} />
              <Route path="/judge/assignments" element={<JudgeAssignmentsPage />} />
              <Route path="/judge/assignments/:assignmentId" element={<JudgeScorePage />} />
              <Route path="/judge/profile" element={<JudgeProfilePage />} />
              <Route path="/judge/history" element={<JudgeHistoryPage />} />

              <Route path="/admin/competitions" element={<CompetitionListPage />} />
              <Route path="/admin/competitions/new" element={<CompetitionBuilderPage />} />
              <Route
                path="/admin/competitions/new/from-template"
                element={<CompetitionCreateFromTemplatePage />}
              />
              <Route path="/admin/competitions/new/import" element={<CompetitionImportPage />} />

              <Route path="/admin/competitions/:competitionId" element={<CompetitionLayout />}>
                <Route index element={<Navigate to="overview" replace />} />
                <Route path="overview" element={<CompetitionOverviewPage />} />
                <Route path="clone" element={<CompetitionClonePage />} />
                <Route path="save-as-template" element={<CompetitionSaveAsTemplatePage />} />
                <Route path="operations" element={<CompetitionOperationsDashboardPage />} />
                <Route path="categories" element={<CompetitionCategoriesPage />} />
                <Route path="rounds" element={<CompetitionRoundsPage />} />
                <Route path="form" element={<CompetitionFormsPage />} />
                <Route path="form/:formId" element={<CompetitionFormEditorPage />} />
                <Route path="teams" element={<CompetitionTeamMembersPage />} />
                <Route path="people" element={<CompetitionPeoplePage />} />
                <Route path="eligibility-reviews" element={<CompetitionEligibilityReviewsPage />} />
                <Route path="participants" element={<CompetitionParticipantsPage />} />
                <Route path="audit" element={<CompetitionAuditPage />} />

                <Route path="submissions" element={<CompetitionSubmissionsPage />} />
                <Route path="judges" element={<CompetitionJudgesPage />} />
                <Route path="judging-load" element={<CompetitionJudgingLoadPage />} />
                <Route path="rubrics" element={<CompetitionRubricsPage />} />
                <Route path="manual-scores" element={<CompetitionManualScoresPage />} />
                <Route path="results" element={<CompetitionResultsPage />} />
                <Route path="files" element={<CompetitionFilesPage />} />
                <Route path="registration-data" element={<CompetitionRegistrationDataPage />} />
                <Route path="announcements" element={<CompetitionAnnouncementsPage />} />
                <Route path="eligibility-data" element={<CompetitionEligibilityDataPage />} />
                <Route path="exports" element={<CompetitionExportCenterPage />} />
              </Route>

              <Route path="/platform" element={<PlatformLayout />}>
                <Route index element={<PlatformDashboardPage />} />
                <Route path="users" element={<PlatformUsersPage />} />
                <Route path="users/:userId" element={<PlatformUserDetailPage />} />
                <Route path="erasure-requests" element={<PlatformErasureRequestsPage />} />
                <Route path="competitions" element={<PlatformCompetitionsPickerPage />} />
                <Route
                  path="competitions/:competitionId/people"
                  element={<PlatformCompetitionPeoplePage />}
                />
                <Route path="judges" element={<PlatformJudgesPage />} />
                <Route path="admins" element={<PlatformAdminsPage />} />
                <Route path="audit" element={<PlatformAuditPage />} />
                <Route path="competition-requests" element={<PlatformCompetitionRequestsPage />} />
                <Route
                  path="competition-requests/:requestId"
                  element={<PlatformCompetitionRequestDetailPage />}
                />
                <Route path="discovery-categories" element={<PlatformDiscoveryCategoriesPage />} />
              </Route>

              <Route path="*" element={<NotFoundPage />} />
            </Route>
          </Routes>
        </Suspense>
      </BrowserRouter>
    </QueryClientProvider>
  )
}
